import { Children, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { createHeadingId } from "@/components/kit/KitMarkdown/toc";
import { toTrackSlug } from "@/utils/track-slug";

const INTERNAL_HOSTS = new Set(["lenacareer.ru", "www.lenacareer.ru", "lena.career"]);

function flattenText(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (child && typeof child === "object" && "props" in child) {
        return flattenText((child as { props?: { children?: ReactNode } }).props?.children ?? null);
      }
      return "";
    })
    .join("")
    .trim();
}

function isExternalLink(href?: string): href is string {
  if (!href || !/^https?:\/\//.test(href)) return false;
  try {
    return !INTERNAL_HOSTS.has(new URL(href).hostname.toLowerCase());
  } catch {
    return false;
  }
}

export interface BlogMarkdownProps {
  body: string;
}

/**
 * The article body renderer: `react-markdown` (the same engine as
 * `KitMarkdown`, no new markdown dep) with the blog editorial component map —
 * anchored H2/H3 (ids match the TOC via the shared `createHeadingId`),
 * brand-dash lists, and a Playfair-italic brand-bar blockquote. Heading anchor
 * ids are generated with the same helper `extractTocHeadings` uses, so the
 * «Содержание» links resolve.
 */
export function BlogMarkdown({ body }: BlogMarkdownProps) {
  const seenIds = new Map<string, number>();

  return (
    <div className="blog-prose" data-component="BlogMarkdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          h2: ({ children }) => (
            <h2 id={createHeadingId(flattenText(children), seenIds)} className="blog-prose-h2">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 id={createHeadingId(flattenText(children), seenIds)} className="blog-prose-h3">
              {children}
            </h3>
          ),
          p: ({ children }) => <p className="blog-prose-p">{children}</p>,
          ul: ({ children }) => <ul className="blog-prose-ul">{children}</ul>,
          ol: ({ children }) => <ol className="blog-prose-ol">{children}</ol>,
          li: ({ children }) => <li className="blog-prose-li">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="blog-prose-quote">{children}</blockquote>
          ),
          hr: () => <hr className="blog-prose-hr" />,
          a: ({ href, children }) => (
            <a
              href={href}
              data-track={`blog-article.link.${toTrackSlug(flattenText(children) || href || "link")}`}
              className="blog-prose-link"
              {...(isExternalLink(href) ? { target: "_blank", rel: "noreferrer" } : {})}
            >
              {children}
            </a>
          ),
          strong: ({ children }) => <strong className="blog-prose-strong">{children}</strong>,
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}

BlogMarkdown.displayName = "BlogMarkdown";
